import type { SidebarFeature } from './roleAccess';
import { isAppointmentOptimizationEnabled } from './features';

/**
 * Persona-grouped navigation for the UI refresh (slice 1, flag `VITE_UI_REFRESH_NAV`).
 *
 * Navigation only: every entry points at a route that already exists and keeps the
 * same `SidebarFeature` role gate the current sidebar uses. Nothing here adds,
 * removes or changes a screen. Every item in the current sidebar has a home below
 * (guarded by `components/layout/__tests__/RefreshSidebarNav.test.tsx`); retiring any of them is an owner decision.
 *
 * Proposed groups without a live destination yet (Brands, Online store) are not
 * listed: an empty link would be a dead end. They arrive with their own slices.
 *
 * `sample: true` marks screens that still fall back to illustrative figures, so the
 * label says so (decision #60); hiding them is a separate owner call (N6).
 */
export interface RefreshNavItem {
  to: string;
  label: string;
  feature: SidebarFeature;
  end?: boolean;
  sample?: boolean;
  /** Hidden when this returns false (build-time feature gate). */
  enabled?: () => boolean;
}

export interface RefreshNavGroup {
  id: string;
  title: string;
  items: ReadonlyArray<RefreshNavItem>;
}

export const REFRESH_NAV_GROUPS: ReadonlyArray<RefreshNavGroup> = [
  {
    id: 'home',
    title: 'Home',
    items: [
      { to: '/app', label: 'Home', feature: 'dashboard', end: true },
      { to: '/app/analytics', label: 'Insights', feature: 'analytics', end: true },
      { to: '/app/analytics/demographics', label: 'Patient demographics', feature: 'analytics' },
      { to: '/app/analytics/revenue', label: 'Revenue', feature: 'analytics' },
      { to: '/app/analytics/practice-optimization', label: 'Practice optimisation', feature: 'analytics' },
      { to: '/app/analytics/appointment-optimization', label: 'Appointment optimisation', feature: 'analytics', enabled: isAppointmentOptimizationEnabled },
      { to: '/app/analytics/contact-lens', label: 'Contact lens insights', feature: 'analytics' },
      { to: '/app/analytics/location', label: 'Location insights', feature: 'analytics', sample: true },
    ],
  },
  {
    id: 'today',
    title: 'Today',
    items: [{ to: '/app/appointments', label: 'Appointments', feature: 'patient_management' }],
  },
  {
    id: 'patients',
    title: 'Patients',
    items: [
      { to: '/app/patients', label: 'Patients', feature: 'patient_management' },
      { to: '/app/marketing', label: 'Recall and campaigns', feature: 'marketing', end: true },
      { to: '/app/marketing/campaigns', label: 'Campaigns', feature: 'marketing' },
      { to: '/app/marketing/segments', label: 'Segments', feature: 'marketing' },
      { to: '/app/marketing/templates', label: 'Templates', feature: 'marketing' },
      { to: '/app/loyalty', label: 'Rewards', feature: 'loyalty', end: true },
      { to: '/app/loyalty/rewards', label: 'Rewards catalogue', feature: 'loyalty' },
      { to: '/app/loyalty/members', label: 'Members', feature: 'loyalty' },
      { to: '/app/loyalty/tiers', label: 'Tiers', feature: 'loyalty', sample: true },
      { to: '/app/loyalty/transactions', label: 'Transactions', feature: 'loyalty' },
    ],
  },
  {
    id: 'frames',
    title: 'Frames',
    items: [
      { to: '/app/inventory', label: 'Stock', feature: 'inventory' },
      { to: '/app/orders', label: 'Glasses orders', feature: 'orders' },
    ],
  },
  {
    id: 'assistant',
    title: 'Practice assistant',
    items: [
      { to: '/app/iris', label: 'Practice assistant', feature: 'iris' },
      { to: '/app/knowledge', label: 'Notes', feature: 'knowledge' },
    ],
  },
  {
    id: 'stores',
    title: 'Stores',
    items: [
      { to: '/app/chain/corporation', label: 'Corporation', feature: 'chain.corporation' },
      { to: '/app/chain/regions', label: 'Regions', feature: 'chain.regions' },
      { to: '/app/chain/districts', label: 'Districts', feature: 'chain.districts' },
      { to: '/app/chain/stores', label: 'Stores', feature: 'chain.stores' },
      { to: '/app/chain/practices', label: 'Practices', feature: 'chain.practices' },
    ],
  },
  {
    id: 'team',
    title: 'Team',
    items: [
      { to: '/app/settings', label: 'Settings', feature: 'settings' },
      { to: '/app/imports', label: 'Import data', feature: 'imports' },
      { to: '/app/faq', label: 'Help', feature: 'settings' },
    ],
  },
];
